/**
 * Quick-access pill bar — surfaces high-value premium features (Investor Mode,
 * Portfolio, Wishlist) on every page they apply to, so users discover them
 * without drilling into the dashboard.
 *
 * Visibility rules:
 *  - Always shown if user is admin or tier ∈ {premium, elite}.
 *  - For free users: shown only on Marketplace / Wishlist with a soft upgrade CTA
 *    so the entry-point is consistent across the app.
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, PieChart, Heart, Crown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const QuickAccessBar = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const tier = (user?.subscription_tier || 'free').toLowerCase();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isPaid = isAdmin || tier === 'premium' || tier === 'elite';

  // Don't render if the user isn't logged in
  if (!user) return null;

  const items = [
    {
      key: 'investor',
      label: 'Investor Mode',
      icon: TrendingUp,
      path: '/investor',
      premium: true,
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      icon: PieChart,
      path: '/portfolio',
      premium: true,
    },
    {
      key: 'wishlist',
      label: 'Wishlist',
      icon: Heart,
      path: '/wishlist',
      premium: false,
    },
  ];

  const onClick = (item) => {
    if (item.premium && !isPaid) {
      navigate('/subscription');
      return;
    }
    navigate(item.path);
  };

  return (
    <div className={`flex flex-wrap gap-2 sm:gap-3 ${className}`} data-testid="quick-access-bar">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
        const locked = item.premium && !isPaid;
        return (
          <button
            key={item.key}
            onClick={() => onClick(item)}
            className={[
              'inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all',
              isActive
                ? 'bg-gold text-black border border-gold'
                : locked
                  ? 'border border-white/10 text-white/45 hover:border-gold/40 hover:text-gold'
                  : 'border border-white/15 text-white/80 hover:border-gold hover:text-gold',
            ].join(' ')}
            data-testid={`quick-access-${item.key}-btn`}
            title={locked ? 'Upgrade to Premium to unlock' : item.label}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="font-medium">{item.label}</span>
            {locked && <Crown className="w-3 h-3 text-gold/80" />}
          </button>
        );
      })}
    </div>
  );
};

export default QuickAccessBar;
